<template>
  <LmDialog v-model="modalShow" :styles="{ minWidth: '900px' }" :title="title">
    <template #toolbar-actions>
      <q-btn :loading="loading" color="tertiary" size="sm" @click="mode === 'edit' ? onUpdate() : onCreate(label)">
        {{ mode === 'edit' ? 'Update' : 'Create' }}
      </q-btn>
    </template>

    <template #content>
      <q-card-section style="max-height: 500px; height: 400px" class="scroll">
        <div class="row justify-end q-col-gutter-md ">
          <div class="col-xs-4">
            <q-input
              id="label_name"
              v-model="label.name"
              required
              label="New license term label"
              type="text"
              outlined
            />
          </div>
          <div class="col-xs-4">
            <enhanced-q-select
              id="label_active"
              v-model="label.default_active"
              :options="activeOptions"
              label="Active for existing and future licenses"
              hint=" Set whether the term is active or not for all the license periods that already exist in the system and for any future license periods."
            />
          </div>
          <div class="col-xs-4">
            <enhanced-q-select
              id="label_status"
              v-model="label.default_license_term_status_id"
              :options="statusOptions"
              label="Status for existing and future licenses"
              hint="Set the status of this term for all the license periods that already exist in the system and for any future license periods."
            />
          </div>
        </div>
      </q-card-section>
    </template>
  </LmDialog>
</template>

<script>
import * as apiTo from '@/modular-api'
// Libraries
import { capitalize } from 'lodash'
// Components
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import EnhancedQSelect from '@components/general/EnhancedQSelect'

export default {
  components: {
    LmDialog,
    EnhancedQSelect
  },

  props: {
    value: Boolean,
    title: String,
    itemToEdit: Object
  },

  data: () => ({
    loading: false,
    label: {
      name: null,
      default_active: null,
      default_license_term_status_id: null
    },
    statuses: null
  }),

  computed: {
    modalShow: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    activeOptions() {
      return [
        {
          label: 'Yes',
          value: true
        },
        {
          label: 'No',
          value: false
        }
      ]
    },

    statusOptions() {
      if (!this.statuses) return
      return this.statuses.map(s => ({
        label: this.displayStatus(s),
        value: s.id
      }))
    },

    mode() {
      if (this.itemToEdit) return 'edit'
      return 'create'
    }
  },

  watch: {
    modalShow(val) {
      val && this.itemToEdit && this.initFields(this.itemToEdit)
    }
  },

  created() {
    apiTo.GET_TERM_LABELS_CREATE().then(({ data }) => (this.statuses = data.result.statuses))
  },

  methods: {
    initFields(data) {
      this.label = {
        name: data.name,
        default_active: data.active,
        default_license_term_status_id: data.status.id
      }
    },

    onCreate(label) {
      this.loading = true
      apiTo
        .CREATE_TERM_LABEL(label)
        .then(({ data }) => {
          this.$emit('created', data.result)
          this.resetModal()
        })
        .finally(() => (this.loading = false))
    },

    onUpdate() {
      const payload = this.getUpdatedFieldsPayload()

      if (!payload) {
        this.$store.dispatch(
          'createNotificationToast',
          {
            message: ['Please update at least one field.'],
            color: 'negative'
          },
          { root: true }
        )
        return
      }

      this.loading = true
      apiTo
        .MODIFY_TERM_LABEL(this.itemToEdit.id, payload)
        .then(({ data }) => {
          this.$emit('updated', data.result)
          this.resetModal()
        })
        .finally(() => (this.loading = false))
    },

    getUpdatedFieldsPayload() {
      const updatedPayload = {}

      if (this.label.name !== this.itemToEdit.name) {
        updatedPayload.name = this.label.name
      }
      if (this.label.default_active !== this.itemToEdit.active) {
        updatedPayload.default_active = this.label.default_active
      }
      if (this.label.default_license_term_status_id !== this.itemToEdit.status.id) {
        updatedPayload.default_license_term_status_id = this.label.default_license_term_status_id
      }

      return Object.keys(updatedPayload).length ? updatedPayload : null
    },

    resetModal() {
      this.modalShow = false
      this.label = {
        name: null,
        default_active: null,
        default_license_term_status_id: null
      }
    },

    displayStatus(s) {
      return s.name === 'n/a' ? s.name.toUpperCase() : capitalize(s.name)
    }
  }
}
</script>
